import { useEffect } from 'react'
import {
  Text,
  Button,
  Heading,
  Paragraph,
  CheckIcon,
} from '@buffer-mono/popcorn'

import { trackUpgradePathViewed } from '../../../../common/hooks/useSegmentTracking'
import { useUser } from '../../../../common/context/User'
import { MODALS } from '../../../../common/types'
import useDismissObject from '../../../../common/hooks/useDismissObject'
import { addMonths } from 'date-fns'

import styles from './TrialExpired.module.css'

// Container component for the modal wrapper
export const TrialExpiredContainer = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  return <div className={styles.trialExpiredContainer}>{children}</div>
}
import trackTrialExpiredModalViewed from '../../../../tracking/TrialPaths/trackTrialExpiredModalViewed'
import trackTrialExpiredModalMainCTAClicked from '../../../../tracking/TrialPaths/trackTrialExpiredModalMainCTAClicked'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

// bodyTextColor is now handled by CSS classes

const benefitCheckPoints = [
  'Schedule up to 2,000 posts',
  'Track audience growth with Analyze',
  'Engage with your audience directly',
  'Collaborate with unlimited users',
  'Save & manage Hashtags easily',
  'Get unlimited tags',
]

export const TrialExpired = (): JSX.Element => {
  const user = useUser()
  const { modalData, openModal, dismissModal: onDismiss } = useModalManager()
  const { onDismissObject } = useDismissObject({
    organizationId: user?.currentOrganization?.id,
    dismissedObject: {
      id: 'trial_expired_modal',
      expiresAt: addMonths(new Date(), 6).toISOString(),
    },
  })

  const { ctaButton, upgradePathName, ctaView, ctaLocation } = modalData || {}

  const defaultUpgradePathName = 'trialExpiredModal-upgrade'
  const payload = {
    ctaButton: ctaButton || 'upgradeOptions',
    upgradePathName: upgradePathName || defaultUpgradePathName,
    ctaView: ctaView || 'trialExpiredModal',
    ctaLocation: ctaLocation || 'trialExpiredModal',
  }

  const onUpgrade = (): void => {
    openModal({
      key: MODALS.planSelector,
      data: {
        cta: 'navBar-trialExpiredModal-trialExpired-1',
        upgradePathName: 'trialExpired-upgrade',
      },
    })
  }

  useEffect(() => {
    trackTrialExpiredModalViewed({
      payload,
      user,
    })
    trackUpgradePathViewed({
      payload: {
        upgradePathName: defaultUpgradePathName,
      },
      user,
    })
  }, [payload, user])

  const handleMainCTAClicked = (): void => {
    trackTrialExpiredModalMainCTAClicked({
      payload,
      user,
    })
    onDismissObject()
    onUpgrade()
  }

  const imageUrl =
    'https://buffer-ui.s3.amazonaws.com/upgrade-paths/trial-expired/social-proof.png'
  const description = `Your free trial has expired and you are back on the limited free plan. Upgrade now to keep enjoying all these benefits and more...`

  return (
    <div className={styles.screenContainer}>
      <div className={styles.sectionLeft}>
        <Heading as="h2" size="large" className={styles.heading}>
          Your trial period is over. What's next?
        </Heading>
        <Paragraph className={styles.description}>{description}</Paragraph>
        <ul className={styles.details}>
          {benefitCheckPoints.map(
            (detail): JSX.Element => (
              <li key={detail}>
                <span className={styles.check}>
                  <CheckIcon size="small" />
                </span>
                <Text className={styles.detailText}>{detail}</Text>
              </li>
            ),
          )}
        </ul>
        <div className={styles.buttonContainer}>
          <Button variant="primary" onClick={handleMainCTAClicked}>
            See Upgrade Options
          </Button>
          <Button
            variant="secondary"
            onClick={(): void => {
              onDismissObject()
              onDismiss()
            }}
          >
            Stay on the Free Plan
          </Button>
        </div>
      </div>
      <div
        className={styles.sectionRight}
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
    </div>
  )
}

export default TrialExpired
